import { motion } from 'framer-motion';
import React from 'react';

import { AnimatePresence, TabContent } from './Animations';
import { cn } from './cn';

export interface TabItem {
  key: string;
  label: string;
  content: React.ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  selectedKey?: string;
  onTabChange?: (key: string) => void;
  className?: string;
}

export const Tabs = ({ items, selectedKey, onTabChange, className }: TabsProps) => {
  const [activeKey, setActiveKey] = React.useState(selectedKey || items[0]?.key);
  // Use internal state always — selectedKey only sets the initial value.
  const currentKey = activeKey;

  const handleChange = (key: string) => {
    setActiveKey(key);
    onTabChange?.(key);
  };

  const activeItem = items.find((item) => item.key === currentKey);

  return (
    <div className={cn('flex flex-col flex-1', className)}>
      <div className="flex border-b border-border relative" role="tablist">
        {items.map((item) => (
          <button
            key={item.key}
            role="tab"
            aria-selected={currentKey === item.key}
            onClick={() => handleChange(item.key)}
            className={cn(
              'relative px-4 py-3 first:pl-0 font-heading text-sm font-medium transition-colors duration-200 cursor-pointer bg-transparent border-none',
              currentKey === item.key ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            {item.label}
            {currentKey === item.key && (
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 rounded-full"
                layoutId="tab-indicator"
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <TabContent key={currentKey} className="flex-1 flex flex-col pt-4" role="tabpanel">
          {activeItem?.content}
        </TabContent>
      </AnimatePresence>
    </div>
  );
};
